import { registerActions, getAction } from "../actionRegistry";
/**
 * Mockup Client (IE Client) Functions
 *
 * Functions for the mockup generation client/interface.
 */

import { api } from "../api";
import { toastSuccess, toastError } from "../ui/messages";
import { modal, closeModalLast, refreshModal } from "../ui/modal";
import { ieclientrefresh } from "./gallery";
import { getDomElement, getDomElementOrThrow, queryAll, setText } from "../utils/dom";

function getSelectedMockups(): { ids: string[]; joined: string } {
  const getter = getAction("__mockupGetSelected") as (() => string[]) | undefined;
  if (getter) {
    const ids = getter();
    return { ids, joined: ids.join(",") };
  }
  // Fallback: legacy DOM-based reading
  const ids: string[] = [];
  queryAll(".interior-select").forEach((el) => {
    const the_id = el.id;
    if (!the_id) return;
    const raw_id = the_id.replace("interior-", "");
    if (getDomElement<HTMLInputElement>(`interior-${raw_id}`)?.value === "1") {
      ids.push(raw_id);
    }
  });
  return { ids, joined: ids.join(",") };
}

export async function ieclientselectsave(): Promise<void> {
  const { ids, joined } = getSelectedMockups();

  try {
    await api.mockupClient.save({ mode: "selected_mockups", mockups: ids });
    toastSuccess("Mockup designs set");
    closeModalLast();
    setText("ieclient-selected", String(ids.length));
    getDomElementOrThrow<HTMLInputElement>("ieclient-selected-mockups").value = joined;
    void ieclientrefresh();
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function ieclientselectsave2(): Promise<void> {
  const { ids, joined } = getSelectedMockups();

  try {
    await api.mockupClient.save({ mode: "selected_mockups", mockups: ids });
    closeModalLast();
    setText("ieclient-selected", String(ids.length));
    getDomElementOrThrow<HTMLInputElement>("ieclient-selected-mockups").value = joined;
    void ieclientrefresh();
    setTimeout(() => {
      void ieclientgenerate2();
    }, 1000);
    setTimeout(() => {
      modal("mockup-generate", "dynamic");
    }, 500);
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function ieclientstartover(): Promise<void> {
  if (!confirm("Are you sure you want to clear everything and start over from scratch?")) return;
  try {
    await api.mockupClient.save({ mode: "startover" });
    toastSuccess("Cleared all data");
    refreshModal();
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function ieclientstartover2(): Promise<void> {
  const stage = getDomElement<HTMLInputElement>("ieclient-stage")?.value;
  if (stage === "1") return;
  try {
    await api.mockupClient.save({ mode: "startover" });
    location.href = "/mockup";
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function ieclientgenerate(): Promise<void> {
  const stage = getDomElement<HTMLInputElement>("ieclient-stage")?.value;
  if (stage === "1") {
    toastError("Generate not available");
    return;
  }
  const files = parseInt(getDomElement<HTMLInputElement>("ieclient-files")?.value ?? "0");
  if (files === 0) {
    toastError("Add files first");
    return;
  }
  const mockups = getDomElement<HTMLInputElement>("ieclient-selected-mockups")?.value;
  if (!mockups) {
    toastError("No mockups selected");
    return;
  }
  try {
    await api.mockupClient.save({ mode: "generate" });
    toastSuccess("Generating mockups");
    void ieclientrefresh();
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function ieclientgenerate2(): Promise<void> {
  const stage = getDomElement<HTMLInputElement>("ieclient-stage")?.value;
  if (stage === "1") {
    toastError("Generate not available");
    return;
  }
  const files = parseInt(getDomElement<HTMLInputElement>("ieclient-files")?.value ?? "0");
  if (files === 0) {
    toastError("Add files first");
    return;
  }
  const mockups = getDomElement<HTMLInputElement>("ieclient-selected-mockups")?.value;
  if (!mockups) {
    toastError("No mockups selected");
    return;
  }
  try {
    await api.mockupClient.save({ mode: "generate" });
    void ieclientrefresh();
  } catch (err: unknown) {
    // Close first. `ieclientselectsave2` opens the mockup-generate modal 500 ms before
    // calling this, and that modal only renders a Close button once the poll reports
    // stage 2 — so a rejected generate (nothing matched the selected aspects, over the
    // pair cap) would otherwise strand the user behind "Generating... Please wait..."
    // with the toast hidden underneath it.
    closeModalLast();
    toastError((err as Error).message);
  }
}

export async function ieclientDeleteFile(id: string, uploadid: string): Promise<void> {
  try {
    // MockupClientFile ids are uuids since the pictufy_ieclient_files cutover.
    await api.mockupClient.delete({ id });
    if (uploadid === "") {
      getDomElement("ieclient-file-" + id)?.remove();
    } else {
      getDomElement("ieclient-file-" + uploadid)?.remove();
    }
    toastSuccess("File removed");
    void ieclientrefresh();
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export function inext(): void {
  const filesEl = getDomElement<HTMLInputElement>("ieclient-files");

  if (filesEl && parseInt(filesEl.value) > 0) {
    modal("mockup-select", "large");
  } else {
    toastError("You must upload source images first");
  }
}

/**
 * Reset the mockup client shortly after the download link is followed, giving
 * the browser time to start the download before the page navigates away.
 * Registered so the download anchor can dispatch it via `data-fn` — it was an
 * inline `onclick="setTimeout(ieclientstartover2,1000)"`, which resolved against
 * `window` and so had been dead since the window bridge was retired.
 */
function ieclientstartover2delayed(): void {
  setTimeout(() => {
    void ieclientstartover2();
  }, 1000);
}

registerActions({
  ieclientselectsave2,
  ieclientstartover2,
  ieclientstartover2delayed,
  ieclientgenerate2,
  ieclientDeleteFile,
  inext,
});
